import React from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiUserCheck, FiActivity, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PatientJourney = () => {
  const steps = [
    {
      icon: FiCalendar,
      title: 'Make Appointment',
      step: 'Step 01',
      description: 'Embark on your journey to wellness by securing your personalized time for care.',
      accent: 'emerald'
    },
    {
      icon: FiUserCheck,
      title: 'Get Consultant',
      step: 'Step 02',
      description: 'Our team of dedicated professionals is here to assist you with a deep diagnostic consultation.',
      accent: 'blue'
    },
    {
      icon: FiActivity,
      title: 'Get Care & Relief',
      step: 'Step 03',
      description: 'Experience a spectrum of services designed to bring comfort, alignment, and healing.',
      accent: 'purple'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:40px_40px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <span className="text-emerald-600 font-bold uppercase tracking-[0.3em] text-xs">The Process</span>
          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mt-4 mb-6 tracking-tight">
            Start Your Health Journey <br className="hidden md:block"/> 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">In Three Simple Steps</span>
          </h2>
          <div className="w-20 h-1.5 bg-emerald-600 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 relative">
          {/* Connecting Line Desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 w-full h-0.5 bg-emerald-100 -z-10 transform -translate-y-1/2"></div>

          {steps.map((step, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative group"
            >
              <div className="bg-white rounded-[2.5rem] p-10 shadow-xl shadow-gray-200/50 border border-white hover:border-emerald-100 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                <div className="absolute -top-6 left-10">
                  <div className={`w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center text-white font-black text-xl bg-emerald-600 group-hover:scale-110 transition-transform duration-500`}>
                    {step.step.split(' ')[1]}
                  </div>
                </div>

                <div className="mt-4 space-y-6">
                  <div className={`w-20 h-20 rounded-3xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-600 transition-colors duration-500`}>
                    <SafeIcon icon={step.icon} className="w-10 h-10 text-emerald-600 group-hover:text-white transition-colors duration-500" />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>

                  <div className="pt-4">
                    <button className="flex items-center space-x-2 text-emerald-600 font-bold text-sm tracking-widest uppercase group-hover:translate-x-2 transition-transform">
                      <span>Learn More</span>
                      <SafeIcon icon={FiArrowRight} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PatientJourney;